import { apiSlice } from "@/shared/api/api-slice"

import type {
  PromptResponse,
  PromptsListResponse,
  PromptsPageParam,
  CreatePromptRequest,
  GetTopicPromptRequest,
  UpdatePromptRequest,
  DeletePromptRequest
} from "@/shared/types"

/**
 * Prompt API endpoints
 */
export const promptApi = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getGroupPrompt: builder.query<PromptResponse, number>({
      query: chatId => `/api/v1/prompts/group/${chatId}`,
      providesTags: (_result, _error, chatId) => [{ type: "Prompt" as const, id: chatId }]
    }),

    createGroupPrompt: builder.mutation<PromptResponse, CreatePromptRequest>({
      query: ({ chatId, request }) => ({
        url: `/api/v1/prompts/group/${chatId}`,
        method: "POST",
        body: request
      }),
      invalidatesTags: (_, error, { chatId }) =>
        error
          ? []
          : [
              { type: "Prompt" as const, id: chatId },
              { type: "PromptsList" as const, id: chatId },
              { type: "DocumentPlacesList" as const, id: chatId }
            ]
    }),

    getTopicPrompt: builder.query<PromptResponse, GetTopicPromptRequest>({
      query: ({ chatId, topicId }) => `/api/v1/prompts/topic/${chatId}/${topicId}`,
      providesTags: (_result, _error, { chatId, topicId }) => [
        { type: "Prompt" as const, id: chatId },
        { type: "TopicPrompt" as const, id: `${chatId}-${topicId}` }
      ]
    }),

    createTopicPrompt: builder.mutation<PromptResponse, CreatePromptRequest>({
      query: ({ chatId, request }) => ({
        url: `/api/v1/prompts/topic/${chatId}`,
        method: "POST",
        body: request
      }),
      invalidatesTags: (_, error, { chatId }) =>
        error
          ? []
          : [
              { type: "PromptsList" as const, id: chatId },
              { type: "TopicPromptsList" as const, id: chatId },
              { type: "DocumentPlacesList" as const, id: chatId }
            ]
    }),

    listGroupPrompts: builder.infiniteQuery<PromptResponse[], number, PromptsPageParam>({
      infiniteQueryOptions: {
        initialPageParam: { page: 1, size: 20 },
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
          if (lastPage.length < lastPageParam.size) {
            return undefined
          }
          return {
            page: lastPageParam.page + 1,
            size: lastPageParam.size
          }
        }
      },
      query: ({ queryArg: chatId, pageParam }) => ({
        url: `/api/v1/prompts/group/${chatId}/list`,
        params: {
          page: pageParam.page,
          size: pageParam.size
        }
      }),
      transformResponse: (response: PromptsListResponse) => response.items,
      providesTags: (_result, _error, chatId) => [{ type: "PromptsList" as const, id: chatId }]
    }),

    listTopicPrompts: builder.infiniteQuery<PromptResponse[], number, PromptsPageParam>({
      infiniteQueryOptions: {
        initialPageParam: { page: 1, size: 20 },
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
          if (lastPage.length < lastPageParam.size) {
            return undefined
          }
          return {
            page: lastPageParam.page + 1,
            size: lastPageParam.size
          }
        }
      },
      query: ({ queryArg: chatId, pageParam }) => ({
        url: `/api/v1/prompts/topic/${chatId}/list`,
        params: {
          page: pageParam.page,
          size: pageParam.size
        }
      }),
      transformResponse: (response: PromptsListResponse) => response.items,
      providesTags: (_result, _error, chatId) => [{ type: "TopicPromptsList" as const, id: chatId }]
    }),

    updatePrompt: builder.mutation<PromptResponse, UpdatePromptRequest>({
      query: ({ chatId, request }) => ({
        url: `/api/v1/prompts/${chatId}/prompt`,
        method: "PATCH",
        body: request
      }),
      invalidatesTags: (_, error, { chatId }) =>
        error
          ? []
          : [
              { type: "Prompt" as const, id: chatId },
              { type: "PromptsList" as const, id: chatId },
              { type: "TopicPromptsList" as const, id: chatId }
            ]
    }),

    deletePrompt: builder.mutation<void, DeletePromptRequest>({
      query: ({ chatId, promptId }) => ({
        url: `/api/v1/prompts/${chatId}/prompt/${promptId}`,
        method: "DELETE"
      }),
      invalidatesTags: (_, error, { chatId }) =>
        error
          ? []
          : [
              { type: "Prompt" as const, id: chatId },
              { type: "PromptsList" as const, id: chatId },
              { type: "TopicPromptsList" as const, id: chatId }
            ]
    })
  })
})

// Export hooks for use in components
export const {
  useGetGroupPromptQuery,
  useCreateGroupPromptMutation,
  useGetTopicPromptQuery,
  useCreateTopicPromptMutation,
  useListGroupPromptsInfiniteQuery,
  useListTopicPromptsInfiniteQuery,
  useUpdatePromptMutation,
  useDeletePromptMutation
} = promptApi
